<template>
  <!-- workOrder_v5 §8：素材分类标签分布·单级横向柱状图（一~四级各一张卡片，页面 2×2 排布） -->
  <div class="level-bar">
    <header>
      <h3>
        {{ title }}
        <em v-if="levelLabel" class="lv">{{ levelLabel }}</em>
      </h3>
      <span>{{ subTitle }}</span>
    </header>
    <div class="full-chart-icon" @click="fullScreen">
      <span class="icon iconfont icon-jt-quanping_o" title="全屏展示"></span>
    </div>
    <div class="chart-box">
      <div :id="domId" ref="chartWrap" class="chart"></div>
      <!-- 后端每级恒返字典全名（可全 0）+ 兜底项，空态按该级总数判 -->
      <div v-if="!levelTotal" class="empty">暂无数据</div>
    </div>
  </div>
</template>
<script>
import * as echarts from "echarts"
import { mapState } from "vuex"

export default {
  props: {
    // 级别 key：lv1 / lv2 / lv3 / lv4
    level: { type: String, default: "lv1" },
    // 级别展示名：一级 / 二级 / 三级 / 四级
    levelLabel: { type: String, default: "" },
    // {lv1:[{name,num}], lv2, lv3, lv4}；每级最后一条是兜底项（一二级「未知」、三四级「无」），后端已排好序
    dataList: { type: Object, default: () => ({}) },
    title: { type: String, default: "素材分类标签分布" },
    subTitle: { type: String, default: "(素材上传时间)" },
  },
  data() {
    return { chart: null }
  },
  computed: {
    ...mapState({ sidebarOpened: (state) => state.app.sidebar.opened }),
    // 四个实例同时在页面时 id 必须唯一，否则全屏取错节点
    domId() {
      return "level-bar-" + this.level
    },
    levelList() {
      return this.dataList && Array.isArray(this.dataList[this.level]) ? this.dataList[this.level] : []
    },
    levelTotal() {
      return this.levelList.reduce((s, i) => s + (+i.num || 0), 0)
    },
  },
  mounted() {
    this.chart = echarts.init(this.$refs.chartWrap)
    this.renderChart()
    window.addEventListener("resize", this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize)
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange)
    document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange)
    this.chart && this.chart.dispose()
  },
  methods: {
    fullScreen() {
      const element = this.$refs.chartWrap
      if (!element) return
      if (element.requestFullscreen) element.requestFullscreen()
      else if (element.webkitRequestFullScreen) element.webkitRequestFullScreen()
      else if (element.mozRequestFullScreen) element.mozRequestFullScreen()
      else if (element.msRequestFullscreen) element.msRequestFullscreen()
      document.addEventListener("fullscreenchange", this.handleFullscreenChange)
      document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange)
    },
    handleFullscreenChange() {
      const element = this.$refs.chartWrap
      if (!element) return
      const isFull = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement
      element.classList.toggle("fullscreen-chart", !!isFull)
      this.$nextTick(() => this.handleResize())
    },
    renderChart() {
      if (!this.chart) return
      const list = this.levelList
      const total = this.levelTotal
      // 横向柱：category 在 y 轴、inverse 让第一条在最上；兜底项（后端放末尾）自然落最下
      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          formatter: (params) => {
            const p = params[0]
            const pct = total > 0 ? ((p.value / total) * 100).toFixed(2) + "%" : "-"
            return `${p.name}<br/>${p.marker}素材数：${p.value}（占比 ${pct}）`
          },
        },
        grid: { left: 4, right: 48, top: 6, bottom: 6, containLabel: true },
        xAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10 }, splitLine: { lineStyle: { type: "dashed" } } },
        yAxis: {
          type: "category",
          inverse: true,
          data: list.map((i) => i.name),
          axisLabel: { fontSize: 11, interval: 0 },
          axisTick: { show: false },
        },
        series: [
          {
            type: "bar",
            barMaxWidth: 16,
            data: list.map((i) => ({
              value: +i.num || 0,
              // 兜底项（未知 / 无）用灰色，字典项蓝色
              itemStyle: { color: i.name === "未知" || i.name === "无" ? "#c0c4cc" : "#557fee" },
            })),
            label: { show: true, position: "right", fontSize: 10, formatter: (p) => p.value },
          },
        ],
      }
      this.chart.setOption(options, true)
    },
    handleResize() {
      this.chart && this.chart.resize()
    },
  },
  watch: {
    dataList: { handler() { this.renderChart() }, deep: true },
    sidebarOpened() {
      setTimeout(() => this.handleResize(), 200)
    },
  },
}
</script>

<style scoped lang="scss">
.level-bar {
  position: relative;
  header {
    display: flex;
    align-items: center;
    h3 { display: flex; align-items: center; }
    .lv {
      margin-left: 8px;
      padding: 1px 8px;
      font-style: normal;
      font-size: 12px;
      font-weight: 500;
      color: #557fee;
      background: #eef2ff;
      border-radius: 10px;
    }
    span { color: #a9afb9; margin-left: 10px; }
  }
  .full-chart-icon {
    width: 30px; height: 30px; position: absolute; right: 0; top: 5px; cursor: pointer; z-index: 1;
  }
  .chart-box {
    position: relative;
  }
  .chart {
    width: 100%;
    height: 300px;
  }
  .empty { position: absolute; left: 0; right: 0; top: 50%; text-align: center; color: #c0c4cc; font-size: 12px; }
  .chart.fullscreen-chart {
    position: fixed; top: 0; left: 0; width: 100% !important; height: 100% !important;
    background-color: #f0f2f5; z-index: 9999; padding: 16px; box-sizing: border-box;
  }
}
</style>
